import React from "react";
import { IconButton, Menu, MenuItem, ListItemIcon } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ReplyIcon from "@mui/icons-material/Reply";
import DeleteIcon from "@mui/icons-material/Delete";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import InsertEmoticonIcon from "@mui/icons-material/InsertEmoticon";
import SendIcon from "@mui/icons-material/Send";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import SearchIcon from "@mui/icons-material/Search";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import BlockIcon from "@mui/icons-material/Block";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import styles from "./massengermodal.module.css";
import dummyUser from "../../../assets/images/dummy/dummy-user.png";

const EmojiPicker = React.lazy(() => import("emoji-picker-react"));

// Wrapper that always shows dummyUser when src is empty or fails to load
const SafeImage = ({ src, alt, style, className }) => {
  const [imgSrc, setImgSrc] = React.useState(src || dummyUser);

  React.useEffect(() => {
    setImgSrc(src || dummyUser);
  }, [src]);

  return (
    <img
      src={imgSrc}
      alt={alt || "user"}
      style={style}
      className={className}
      onError={() => setImgSrc(dummyUser)}
    />
  );
};

const CenterSection = ({
  selectedUser,
  onBackToList,
  isChatsLoading = false,
  isMessagesLoading = false,
  currentProfileId,
  safeGetAsset,
  getAvatarUrl,
  safeRender,
  switchedProfile,
  messageAreaRef,
  chatHistory,
  name,
  handleMenuOpen,
  anchorEl,
  activeMessage,
  handleMenuClose,
  formatTime,
  showEmojiPicker,
  emojiPickerRef,
  onEmojiClick,
  toggleButtonRef,
  setShowEmojiPicker,
  handleFileUpload,
  messageInputRef,
  sendMessage,
  typingStatus,
  onlineStatus,
  replyingTo,
  setReplyingTo,
  handleDeleteMessage,
  onClearChat,
  isContactBlocked: blockedFromServer,
  isContactBlockStatusLoaded,
  onToggleContactBlock,
  selectedFile,
  filePreview,
  isUploadingMedia = false,
  handleRemoveSelectedFile,
  handleSend,
}) => {
  const fileInputRef = React.useRef(null);
  const [headerMenuAnchor, setHeaderMenuAnchor] = React.useState(null);
  const [isSearchingMessages, setIsSearchingMessages] = React.useState(false);
  const [messageSearch, setMessageSearch] = React.useState("");
  const [isConversationMuted, setIsConversationMuted] = React.useState(false);
  const [isContactBlocked, setIsContactBlocked] = React.useState(false);
  const chatId = String(selectedUser?._id || "");
  const profileId = String(currentProfileId || "");
  const getPreferenceKey = (preference) =>
    `messenger:${profileId}:${chatId}:${preference}`;

  React.useEffect(() => {
    setMessageSearch("");
    setIsSearchingMessages(false);
    setIsConversationMuted(
      localStorage.getItem(getPreferenceKey("muted")) === "true",
    );
  }, [chatId, profileId]);

  React.useEffect(() => {
    if (!isContactBlockStatusLoaded) return;
    setIsContactBlocked(Boolean(blockedFromServer));
  }, [blockedFromServer, chatId, isContactBlockStatusLoaded, profileId]);

  const getOnlineStatusText = () => {
    const targetUserId = selectedUser?.otherParticipantData?._id
      || selectedUser?.otherParticipantData?.userid
      || selectedUser?.otherParticipantData?.shopid;

    if (!targetUserId) return null;
    const info = onlineStatus[targetUserId];
    if (!info) return null; // unknown status

    if (info.status === 'online') {
      return <span style={{ color: '#28a745', fontSize: '13px', fontWeight: '500' }}>● Online</span>;
    }

    if (info.lastSeen) {
      const diffMs = Date.now() - info.lastSeen;
      const diffMins = Math.floor(diffMs / 60000);
      const currentHour = new Date().getHours();
      const isNight = currentHour >= 22 || currentHour < 6;

      let text = '';
      if (isNight && diffMins > 10) {
        text = 'Away';
      } else if (diffMins < 1) {
        text = 'Last seen: just now';
      } else if (diffMins < 60) {
        text = `Last seen: ${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
      } else if (diffMins < 1440) {
        const hours = Math.floor(diffMins / 60);
        text = `Last seen: ${hours} hour${hours > 1 ? 's' : ''} ago`;
      } else {
        const days = Math.floor(diffMins / 1440);
        text = `Last seen: ${days} day${days > 1 ? 's' : ''} ago`;
      }
      return <span style={{ color: '#6c757d', fontSize: '13px' }}>{text}</span>;
    }
    return null;
  };
  const currentMessages = chatHistory[selectedUser?._id] || [];
  const formatDateLabel = (date) => {
    if (date.toDateString() === new Date().toDateString()) return "Today";

    const today = new Date();
    const messageDay = new Date(date);
    const dayDifference = Math.floor(
      (Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()) -
        Date.UTC(
          messageDay.getFullYear(),
          messageDay.getMonth(),
          messageDay.getDate(),
        )) /
        86400000,
    );
    if (dayDifference === 1) return "Yesterday";

    return date.toLocaleDateString(undefined, {
      weekday: "long",
      month: "long",
      day: "numeric",
      ...(dayDifference >= 7 ? { year: "numeric" } : {}),
    });
  };
  return (
    <div className={styles.centerSection}>
      {!selectedUser?.otherParticipantData && isChatsLoading ? (
        <div
          className={styles.centerLoadingState}
          role="status"
          aria-label="Loading conversations"
          aria-busy="true"
        >
          <div className={styles.centerLoadingHeader}>
            <span className={styles.centerLoadingAvatar} />
            <span className={styles.centerLoadingHeaderLines}>
              <span />
              <span />
            </span>
          </div>
          <div className={styles.centerLoadingMessages}>
            <span className={styles.centerLoadingMessage} />
            <span className={`${styles.centerLoadingMessage} ${styles.centerLoadingMessageOwn}`} />
            <span className={styles.centerLoadingMessageShort} />
          </div>
        </div>
      ) : selectedUser?.otherParticipantData ? (
        <>
          <div
            className={styles.personInfo}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              {onBackToList && (
                <button
                  type="button"
                  className={styles.backToListBtn}
                  onClick={onBackToList}
                  aria-label="Back to contacts list"
                  title="Back to list"
                >
                  <ArrowBackIcon style={{ fontSize: 18 }} />
                </button>
              )}
              <SafeImage
                src={safeGetAsset(getAvatarUrl(selectedUser?.otherParticipantData))}
                alt="User"
                className={styles.userprofileImage}
              />
              <div className={styles.personDetails}>
                <h6 className={styles.leftinfop}>
                  {safeRender(
                    selectedUser?.otherParticipantData?.fullname ||
                    selectedUser?.otherParticipantData?.shopname ||
                    selectedUser?.otherParticipantData?.name
                  )}
                </h6>
                <p className={styles.leftinfop}>
                  {safeRender(selectedUser?.otherParticipantData?.position)}
                </p>
                {getOnlineStatusText()}
              </div>
            </div>
            <div className={styles.personHeaderActions}>
              <div className={styles.rightInfo}>
                <p className={styles.rightinfop}>
                  {safeRender(selectedUser?.otherParticipantData?.businessType)}
                </p>
                <p className={styles.rightinfop}>
                  {safeRender(
                    switchedProfile?.businessType === "generalPublic"
                      ? selectedUser?.otherParticipantData?.publicAddress || selectedUser?.otherParticipantData?.address
                      : selectedUser?.otherParticipantData?.address
                  )}
                </p>
              </div>
              <IconButton
                aria-label="Conversation options"
                aria-controls={headerMenuAnchor ? "conversation-options-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={Boolean(headerMenuAnchor)}
                onClick={(event) => setHeaderMenuAnchor(event.currentTarget)}
              >
                <MoreHorizIcon />
              </IconButton>
            </div>
            <Menu
              id="conversation-options-menu"
              anchorEl={headerMenuAnchor}
              open={Boolean(headerMenuAnchor)}
              onClose={() => setHeaderMenuAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem onClick={() => {
                setIsSearchingMessages((open) => !open);
                setHeaderMenuAnchor(null);
              }}>
                <ListItemIcon><SearchIcon fontSize="small" /></ListItemIcon>
                Search messages
              </MenuItem>
              <MenuItem onClick={() => {
                const nextMuted = !isConversationMuted;
                localStorage.setItem(getPreferenceKey("muted"), String(nextMuted));
                setIsConversationMuted(nextMuted);
                setHeaderMenuAnchor(null);
              }}>
                <ListItemIcon><NotificationsOffIcon fontSize="small" /></ListItemIcon>
                {isConversationMuted ? "Unmute notifications" : "Mute notifications"}
              </MenuItem>
              <MenuItem onClick={() => {
                if (window.confirm("Clear this conversation from your view? The other participant's messages will not be deleted.")) {
                  onClearChat?.();
                }
                setHeaderMenuAnchor(null);
              }}>
                <ListItemIcon><DeleteSweepIcon fontSize="small" /></ListItemIcon>
                Clear chat
              </MenuItem>
              <MenuItem disabled={!isContactBlockStatusLoaded} onClick={async () => {
                const nextBlocked = !isContactBlocked;
                const didUpdate = await onToggleContactBlock?.(nextBlocked);
                if (didUpdate) {
                  setIsContactBlocked(nextBlocked);
                }
                setHeaderMenuAnchor(null);
              }}>
                <ListItemIcon><BlockIcon fontSize="small" /></ListItemIcon>
                {isContactBlocked ? "Unblock contact" : "Block contact"}
              </MenuItem>
            </Menu>
          </div>

          {isSearchingMessages && (
            <div className={styles.conversationSearch}>
              <SearchIcon fontSize="small" />
              <input
                type="search"
                value={messageSearch}
                onChange={(event) => setMessageSearch(event.target.value)}
                placeholder="Search this conversation..."
                aria-label="Search messages in this conversation"
              />
              <IconButton
                size="small"
                aria-label="Close message search"
                onClick={() => {
                  setIsSearchingMessages(false);
                  setMessageSearch("");
                }}
              >
                <span aria-hidden="true">×</span>
              </IconButton>
            </div>
          )}

          <div className={styles.messageArea} ref={messageAreaRef}>
            {isMessagesLoading ? (
              <div
                className={styles.messageLoadingMessages}
                role="status"
                aria-label="Loading messages"
                aria-busy="true"
              >
                <span className={styles.centerLoadingMessage} />
                <span
                  className={`${styles.centerLoadingMessage} ${styles.centerLoadingMessageOwn}`}
                />
                <span className={styles.centerLoadingMessageShort} />
              </div>
            ) : currentMessages.map((message, index) => {
              if (
                messageSearch.trim() &&
                !String(message.content || "")
                  .toLowerCase()
                  .includes(messageSearch.trim().toLowerCase())
              ) {
                return null;
              }
              const messageDateValue = message.timestamp || message.createdAt;
              const messageDate = messageDateValue
                ? new Date(messageDateValue)
                : null;
              const previousMessage = currentMessages[index - 1];
              const previousDateValue =
                previousMessage?.timestamp || previousMessage?.createdAt;
              const previousDate = previousDateValue
                ? new Date(previousDateValue)
                : null;
              const isValidMessageDate =
                messageDate && !Number.isNaN(messageDate.getTime());
              const isNewCalendarDay =
                isValidMessageDate &&
                previousDate &&
                !Number.isNaN(previousDate.getTime()) &&
                messageDate.toDateString() !== previousDate.toDateString();
              const isAfter24HourGap =
                message.isAfter24HourGap ||
                (isValidMessageDate &&
                  previousDate &&
                  !Number.isNaN(previousDate.getTime()) &&
                  messageDate.getTime() - previousDate.getTime() >
                    24 * 60 * 60 * 1000);
              const showDateDivider =
                isValidMessageDate &&
                (index === 0 || isNewCalendarDay || isAfter24HourGap);
              const otherId = String(
                selectedUser?.otherParticipantData?._id ||
                selectedUser?.otherParticipantData?.shopid ||
                selectedUser?.otherParticipantData?.userid ||
                ""
              );
              const senderId = String(message.sender?._id || message.sender || "");
              const isMyMessage = otherId
                ? senderId !== otherId
                : senderId === String(name?._id);

              return (
                <React.Fragment key={index}>
                  {showDateDivider && (
                    <div className={styles.dateDivider}>
                      <span>{formatDateLabel(messageDate)}</span>
                    </div>
                  )}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-end',
                      justifyContent: isMyMessage ? 'flex-end' : 'flex-start',
                      marginBottom: '15px'
                    }}
                  >
                    {!isMyMessage && (
                      <SafeImage
                        src={safeGetAsset(getAvatarUrl(selectedUser?.otherParticipantData))}
                        alt="User"
                        className={styles.messageAvatar}
                        style={{ width: '32px', height: '32px', borderRadius: '50%', marginRight: '8px', objectFit: 'cover' }}
                      />
                    )}

                  <div className={`${styles.messageStack} ${isMyMessage ? styles.messageStackOwn : ""}`}>
                    <div className={styles.messageMeta}>
                      <span>
                        {isMyMessage
                          ? switchedProfile?.shopname ||
                            switchedProfile?.name ||
                            name?.fullname ||
                            "You"
                          : selectedUser?.otherParticipantData?.displayName ||
                            selectedUser?.otherParticipantData?.fullname ||
                            selectedUser?.otherParticipantData?.shopname ||
                            "Contact"}
                      </span>
                      <span>{formatTime(message.timestamp || message.createdAt)}</span>
                    </div>
                  <div
                    className={`${styles.messageBox} ${isMyMessage ? styles.myMessage : styles.otherMessage}`}
                    style={{ marginBottom: 0, position: 'relative', margin: 0, opacity: message.isDeleted ? 0.7 : 1 }}
                  >
                    <div className={styles.messageContent} style={{ flexDirection: 'column', alignItems: isMyMessage ? 'flex-end' : 'flex-start' }}>

                      {/* Reply Context Block */}
                      {message.replyTo && (
                        <div style={{
                          backgroundColor: 'rgba(0,0,0,0.1)',
                          padding: '6px 10px',
                          borderRadius: '6px',
                          marginBottom: '6px',
                          fontSize: '13px',
                          borderLeft: `3px solid ${isMyMessage ? '#fff' : '#007bff'}`,
                          maxWidth: '100%',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis'
                        }}>
                          <strong>{isMyMessage ? "You" : (selectedUser?.otherParticipantData?.displayName || selectedUser?.otherParticipantData?.shopname || "Other")}</strong><br />
                          {message.replyTo.isDeleted ? <em>This message is deleted</em> : message.replyTo.content}
                        </div>
                      )}

                      <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        {isMyMessage && !message.isDeleted && (
                          <IconButton
                            onClick={(e) => handleMenuOpen(e, index)}
                            size="small"
                            sx={{ marginRight: "8px", padding: "2px" }}
                          >
                            <MoreVertIcon sx={{ fontSize: '18px', color: '#000' }} />
                          </IconButton>
                        )}

                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                          {message.media && !message.isDeleted && (() => {
                            const mediaSrc = safeGetAsset(message.media);
                            return (
                              <div style={{ marginBottom: message.content ? '8px' : '0px' }}>
                                <img
                                  src={mediaSrc}
                                  alt="attachment"
                                  style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px', cursor: 'pointer', objectFit: 'cover' }}
                                  onClick={() => window.open(mediaSrc, '_blank')}
                                />
                                </div>
                            );
                          })()}
                          <p className={styles.messageText} style={{ fontStyle: message.isDeleted ? 'italic' : 'normal', margin: 0 }}>
                            {message.isDeleted
                              ? "This message was deleted"
                              : safeRender(message.content)}
                          </p>
                        </div>

                        {!isMyMessage && !message.isDeleted && (
                          <IconButton
                            onClick={(e) => handleMenuOpen(e, index)}
                            size="small"
                            sx={{ marginLeft: "8px", padding: "2px" }}
                          >
                            <MoreVertIcon sx={{ fontSize: '18px' }} />
                          </IconButton>
                        )}
                      </div>

                      <span className={styles.timestamp} style={{ marginTop: '4px' }}>
                        {formatTime(message.timestamp || message.createdAt)}
                      </span>
                    </div>

                    {isMyMessage && !message.isDeleted && (
                      <div style={{ fontSize: '12px', textAlign: 'right', marginTop: '2px', color: message.status === 'read' ? '#28a745' : '#e0e0e0', fontWeight: '500' }}>
                        {message.status === 'read' ? 'Seen ✓✓' : 'Sent ✓'}
                      </div>
                    )}

                    <Menu
                      anchorEl={anchorEl}
                      open={activeMessage === index}
                      onClose={handleMenuClose}
                      PaperProps={{
                        style: { maxHeight: 600, width: "150px" },
                      }}
                    >
                      <MenuItem onClick={() => {
                        setReplyingTo(message);
                        handleMenuClose();
                      }}>
                        <ListItemIcon> <ReplyIcon fontSize="small" /> </ListItemIcon> Reply
                      </MenuItem>

                      {isMyMessage && (
                        <MenuItem onClick={() => {
                          handleDeleteMessage(message._id);
                        }}>
                          <ListItemIcon> <DeleteIcon fontSize="small" color="error" /> </ListItemIcon> <span style={{ color: 'red' }}>Delete</span>
                        </MenuItem>
                      )}
                    </Menu>
                  </div>
                  </div>

                  {isMyMessage && (
                    <SafeImage
                      src={safeGetAsset(getAvatarUrl(name))}
                      alt="Me"
                      className={styles.messageAvatar}
                      style={{ width: '32px', height: '32px', borderRadius: '50%', marginLeft: '8px', objectFit: 'cover' }}
                    />
                  )}
                  </div>
                </React.Fragment>
              )
            })}
            {typingStatus?.[selectedUser?._id] && (
              <div className={`${styles.messageBox} ${styles.otherMessage}`}>
                <div className={styles.messageContent} style={{ padding: '8px 12px', fontStyle: 'italic', color: '#888' }}>
                  Typing...
                </div>
              </div>
            )}
            {!isMessagesLoading && currentMessages.length === 0 && (
              <div className={styles.chatStartNotice}>
                <span className={styles.chatStartIcon}>
                  <ChatBubbleOutlineIcon />
                </span>
                <span>
                  START OF DIRECT COMMUNICATION WITH{" "}
                  {safeRender(
                    selectedUser?.otherParticipantData?.shopname ||
                      selectedUser?.otherParticipantData?.fullname ||
                      selectedUser?.otherParticipantData?.name
                  ).toUpperCase()}
                </span>
                <small>All enterprise communications are encrypted &amp; logged for operational compliance.</small>
              </div>
            )}
          </div>

          {showEmojiPicker && (
            <div
              className={styles.emojiPicker}
              ref={emojiPickerRef}
            >
              <React.Suspense fallback={<div className="p-3 text-center bg-white rounded">Loading...</div>}>
                <EmojiPicker
                  onEmojiClick={onEmojiClick}
                  width="100%"
                  height={340}
                  previewConfig={{ showPreview: false }}
                  skinTonesDisabled
                  searchPlaceHolder="Search emoji..."
                />
              </React.Suspense>
            </div>
          )}

          {/* Replying To Indicator */}
          {replyingTo && (
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '8px 15px',
              backgroundColor: '#e9ecef',
              borderTop: '1px solid #ced4da',
              fontSize: '14px'
            }}>
              <div>
                <strong>Replying to: </strong>
                <span style={{ color: '#555', fontStyle: 'italic' }}>
                  {safeRender(replyingTo.content).substring(0, 50)}...
                </span>
              </div>
              <IconButton size="small" onClick={() => setReplyingTo(null)}>
                <span style={{ fontSize: '18px', fontWeight: 'bold' }}>&times;</span>
              </IconButton>
            </div>
          )}

          {/* Bottom Message Input area */}
          <div className={styles.messageInput}>
            {/* Attachment Staged Preview */}
            {selectedFile && (
              <div className={styles.attachmentPreviewContainer}>
                <div className={styles.attachmentPreviewContent}>
                  {filePreview ? (
                    <div className={styles.attachmentImageWrapper}>
                      <img
                        src={filePreview}
                        alt="attachment preview"
                        className={styles.attachmentThumbnail}
                      />
                    </div>
                  ) : (
                    <div className={styles.attachmentFileBadge}>
                      <AttachFileIcon style={{ fontSize: 20, color: "#214ce7" }} />
                    </div>
                  )}
                  <div className={styles.attachmentMeta}>
                    <span className={styles.attachmentFileName} title={selectedFile.name}>
                      {selectedFile.name}
                    </span>
                    {selectedFile.size ? (
                      <span className={styles.attachmentFileSize}>
                        {(selectedFile.size / 1024).toFixed(1)} KB
                      </span>
                    ) : null}
                  </div>
                </div>
                <button
                  type="button"
                  className={styles.attachmentRemoveButton}
                  onClick={handleRemoveSelectedFile}
                  disabled={isUploadingMedia}
                  title="Remove attachment"
                  aria-label="Remove attachment"
                >
                  &times;
                </button>
              </div>
            )}

            <div className={styles.inputWrapper}>
              <button
                className={styles.attachmentButton}
                type="button"
                onClick={() => {
                  if (fileInputRef.current) {
                    fileInputRef.current.click();
                  } else {
                    document.getElementById("fileInput")?.click();
                  }
                }}
              >
                <AttachFileIcon />
              </button>
              <input
                ref={fileInputRef}
                id="fileInput"
                type="file"
                style={{ display: "none" }}
                onChange={(e) => {
                  if (handleFileUpload) {
                    handleFileUpload(e.target.files);
                  }
                  e.target.value = "";
                }}
              />
              <input
                type="text"
                className={styles.messageFieldInput}
                ref={messageInputRef}
                placeholder={isContactBlocked ? "Unblock this contact to send messages" : "Type a message here..."}
                disabled={isContactBlocked || isUploadingMedia}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !isContactBlocked && !isUploadingMedia) {
                    e.preventDefault();
                    if (handleSend) {
                      handleSend(messageInputRef.current ? messageInputRef.current.value : e.target.value);
                    } else {
                      sendMessage(e.target.value);
                      e.target.value = "";
                    }
                  }
                }}
              />

              <InsertEmoticonIcon
                className={styles.emojiButton}
                style={{ color: "#007bff", cursor: "pointer" }}
                ref={toggleButtonRef}
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              />
              <button
                type="button"
                className={styles.sendButton}
                disabled={isContactBlocked || isUploadingMedia}
                onClick={() => {
                  if (handleSend) {
                    handleSend(messageInputRef.current ? messageInputRef.current.value : "");
                  } else if (messageInputRef.current) {
                    sendMessage(messageInputRef.current.value);
                    messageInputRef.current.value = "";
                  }
                }}
              >
                {isUploadingMedia ? (
                  "Sending..."
                ) : (
                  <>
                    Send <SendIcon />
                  </>
                )}
              </button>
            </div>
          </div>
        </>
      ) : (
        <div className={styles.emptyChatState}>
          <div className={styles.emptyChatIcon} aria-hidden="true">
            <ChatBubbleOutlineIcon />
          </div>
          <h2 className={styles.emptyChatTitle}>Your conversations, all in one place</h2>
          <p className={styles.emptyChatDescription}>
            Choose a chat from the left to view messages and continue the conversation.
          </p>
        </div>
      )}
    </div>
  );
};

export { CenterSection };
export default CenterSection;
